import React, { useEffect, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { BadgeCheck, Loader2, ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

const SEL = 'h-9 w-full rounded-lg border border-input bg-background px-2 text-sm text-foreground';

// Super admin flag (set from the workspace context) so dialogs know to ask for the brand.
let SUPER = false; let CID = '';
export const setBrandSuper = (v, cid = '') => { SUPER = !!v; if (cid !== CID) { CID = cid || ''; window.dispatchEvent(new Event('tagws-brand-ctx')); } };
// Brands the super admin must choose from on new lists / approvals / sales / manual tags ([] for others).
export const usePickBrands = () => useBrands(SUPER);

// Brands of the open company (a brand user gets only their own brand).
export function useBrands(on = true, key = '') {
  const [brands, setBrands] = useState([]);
  const [ver, setVer] = useState(0);
  useEffect(() => { const f = () => setVer((v) => v + 1); window.addEventListener('tagws-brand-ctx', f); return () => window.removeEventListener('tagws-brand-ctx', f); }, []);
  useEffect(() => { if (on) notesApi.get('/notes/tag-workspace/brands').then(({ data }) => setBrands(data || [])).catch(() => setBrands([])); else setBrands([]); }, [on, key, ver]);
  return brands;
}

// The single brand shared by these pieces ('' when mixed or unbranded).
export const commonBrand = (pieces) => { const s = new Set((pieces || []).map((p) => p?.brand_id || '')); return s.size === 1 ? [...s][0] : ''; };

export const BrandChip = ({ name, testid = 'tagws-brand-chip' }) => (name ? (
  <span className="inline-flex w-fit items-center gap-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[length:calc(var(--fs,1)*0.65625rem)] font-semibold text-indigo-800 dark:text-indigo-300" data-testid={testid}><BadgeCheck size={11} /> {name}</span>
) : null);

// Super admin picks the brand of a new list / approval / sale / manual tag.
export function BrandPick({ brands, value, onChange, testid = 'tagws-brand-pick', label = 'Brand' }) {
  if (!brands.length) return null;
  return (
    <label className="block space-y-1" data-testid={`${testid}-wrap`}>
      <span className="text-xs font-semibold text-muted-foreground">{label} <span className="text-red-600">*</span></span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={SEL} data-testid={testid}>
        <option value="">Choose the brand…</option>
        {brands.map((b) => <option key={b.id} value={b.id}>{b.name}{b.active ? '' : ' (disabled)'}</option>)}
      </select>
    </label>
  );
}

// Brand filter on registers / main page (super admin).
export function BrandFilter({ brands, value, onChange, testid = 'tagws-brand-filter' }) {
  if (brands.length < 1) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} title="Show one brand"
      className={`h-9 max-w-[160px] rounded-lg border px-2 text-sm text-foreground ${value ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/5 font-medium' : 'border-border bg-background'}`} data-testid={testid}>
      <option value="">All brands</option>
      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </select>
  );
}

// Users admin: the brands a company user may see (one or more).
const coBrands = new Map(); // company id -> request, shared by every user row
export function BrandCell({ u, onPatch }) {
  const [brands, setBrands] = useState(null);
  useEffect(() => {
    if (!u.company_id) return;
    if (!coBrands.has(u.company_id)) coBrands.set(u.company_id, notesApi.get(`/notes/tag-workspace/admin/brands/${u.company_id}`).then(({ data }) => data || []).catch(() => { coBrands.delete(u.company_id); return []; }));
    coBrands.get(u.company_id).then(setBrands);
  }, [u.company_id]);
  if (!u.company_id) return <span className="text-xs text-muted-foreground">—</span>;
  if (!brands) return <Loader2 size={13} className="mx-auto animate-spin text-muted-foreground" />;
  const ids = (u.tag_brand_ids?.length ? u.tag_brand_ids : u.tag_brand_id ? [u.tag_brand_id] : []).filter((i) => brands.some((b) => b.id === i));
  const names = ids.map((i) => brands.find((b) => b.id === i)?.name).filter(Boolean);
  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onPatch({ tag_brand_ids: next }, next.length ? `Brands: ${next.map((i) => brands.find((b) => b.id === i)?.name).join(', ')}.` : 'Brands removed — the user now sees nothing.');
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`${SEL} inline-flex h-8 min-w-[120px] max-w-[220px] items-center justify-between gap-1 text-left ${ids.length ? '' : 'border-red-400 text-red-700'}`} title={names.join(', ')} data-testid="tagws-admin-user-brand-select">
          <span className="truncate">{names.length ? names.join(', ') : 'No brand (sees nothing)'}</span><ChevronDown size={13} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[400] w-56 p-2" align="start" data-testid="tagws-admin-user-brand-menu">
        <div className="mb-1 px-1 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Brands this user sees</div>
        {brands.map((b) => (
          <label key={b.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary" data-testid={`tagws-admin-user-brand-opt-${b.name}`}>
            <Checkbox checked={ids.includes(b.id)} onCheckedChange={() => toggle(b.id)} />{b.name}{b.active ? '' : <span className="text-xs text-muted-foreground">(disabled)</span>}
          </label>))}
        {!brands.length ? <p className="px-2 py-1.5 text-xs text-muted-foreground">This company has no brands yet.</p> : null}
      </PopoverContent>
    </Popover>
  );
}

// Users admin: give older pieces with no brand the brand RFJ.
export function BrandBackfill() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => notesApi.get('/notes/tag-workspace/admin/brand-backfill').then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st || !st.missing) return null;
  const run = async () => {
    setBusy(true);
    try { const { data } = await notesApi.post('/notes/tag-workspace/admin/brand-backfill'); toast.success(`${data.updated} piece(s) now carry brand ${st.brand}.`); load(); }
    catch (err) { toast.error(extractError(err, 'Could not fill the brands.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="tagws-brand-backfill">
      <span><b data-testid="tagws-brand-backfill-count">{st.missing}</b> piece(s) have no brand yet.</span>
      <button type="button" disabled={busy} onClick={run} className="rounded-lg bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-60" data-testid="tagws-brand-backfill-run">{busy ? 'Filling…' : `Give them brand ${st.brand}`}</button>
    </div>
  );
}

// Jewellery card: brand chip; the super admin can change it (also changes it in the main portal).
export function CardBrand({ item, brands, onSaved }) {
  const [edit, setEdit] = useState(false);
  const save = async (bid) => {
    setEdit(false);
    if (!bid || bid === item.brand_id) return;
    try { const { data } = await notesApi.patch('/notes/tag-workspace/pieces/brand', { ids: [item.id], brand_id: bid }); onSaved(item.id, data.brand_id, data.brand_name); toast.success(`Brand changed to ${data.brand_name}.`); }
    catch (err) { toast.error(extractError(err, 'Could not change the brand.')); }
  };
  if (!onSaved || !brands?.length) return <BrandChip name={item.brand_name} testid="tagws-card-brand" />;
  if (edit) return (
    <select autoFocus value={item.brand_id || ''} onChange={(e) => save(e.target.value)} onBlur={() => setEdit(false)} className="h-7 w-fit rounded-md border border-input bg-background px-1.5 text-xs text-foreground" data-testid="tagws-card-brand-select">
      {item.brand_id ? null : <option value="">No brand</option>}
      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </select>
  );
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEdit(true); }} title="Change the brand (also in the main portal)" className="w-fit text-left" data-testid="tagws-card-brand-edit">
      {item.brand_name ? <BrandChip name={item.brand_name} testid="tagws-card-brand" /> : <span className="rounded-md border border-dashed border-red-400 px-1.5 py-0.5 text-[length:calc(var(--fs,1)*0.65625rem)] font-semibold text-red-700" data-testid="tagws-card-brand">No brand</span>}
    </button>
  );
}

